import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import useFullscreen from '../../../../../../b2c/shared/hooks/useFullscreen';
import { SceneMode } from '../../../../../3dEngine/core/SceneMode';
import ThreejsScene from '../../../../../3dEngine/threejs/ThreejsScene';
import Icon from '../../../../../shared/components/Icon/Icon';
import ThreeDotsLoader from '../../../../../shared/components/Loaders/ThreeDotsLoader';
import ModularConfiguratorCoordinatorState from '../../ModularConfiguratorCoordinatorState';
import css from './ModularConfiguratorPreview.css';
import ModularConfiguratorToolbar from './ModularConfiguratorToolbar/ModularConfiguratorToolbar';

interface ModularConfiguratorPreviewProps {
  state: ModularConfiguratorCoordinatorState;
  className?: string;
  readonly?: boolean;
  showPerformance?: boolean;
}

const ModularConfiguratorPreview = observer(
  ({ className, state, readonly = false, showPerformance = false }: ModularConfiguratorPreviewProps) => {
    const ref = useRef<HTMLDivElement | null>(null);

    useResizeDetector({ targetRef: ref });

    const fullscreen = useFullscreen(ref);
    const is3dMode = state.configurator.scene.mode === SceneMode.Scene3d;

    return (
      <div ref={ref} className={classNames(css.container, className)}>
        <ModularConfiguratorToolbar className={css.toolbar} fullscreen={fullscreen} state={state} hidden={readonly} />
        <ThreejsScene
          className={css.scene}
          state={state.configurator.scene}
          onObjectClick={(o) => state.configurator.selectComponent(o)}
          onNoneObjectClick={() => state.configurator.unselectComponent()}
          freeze={true}
          showPerformance={showPerformance}
        />
        {!readonly && (
          <div className={css.hint}>
            {!state.configurator.selectedComponent && (
              <div className={css.configure}>
                <Icon className={css.icon} name="click" />
                Click module to edit style
              </div>
            )}
            <div className={css.controls}>
              {fullscreen.isActive
                ? 'Scroll to zoom, drag to pan'
                : `Scroll to zoom${is3dMode ? ', drag to rotate' : ''}, click to select`}
            </div>
          </div>
        )}

        {state.configurator.loadingIndicator.isLoading && (
          <div className={css.loader}>
            <ThreeDotsLoader className={css.inner} size="large" />
          </div>
        )}
      </div>
    );
  },
);

export default ModularConfiguratorPreview;
